/*
 * list.tsx
 * This dealer list Component is used for representing all the saved dealers in list Form.
 */
// Import necessary modules and components
import { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Drawer,
  Input,
  InputRef,
  Select,
  Space,
  Table,
  TableColumnsType,
  TableColumnType,
  Tag,
  TablePaginationConfig,
  Tooltip,
  DatePicker,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
  FilterFilled,
  FilterOutlined,
  IdcardOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Highlighter from "react-highlight-words";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { UserContext } from "../../../context";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { PageHeader } from "../../../components/page-header/page-header";
import dayjs, { Dayjs } from "dayjs";

import {
  TDropdownOptions,
  TResponseModel,
  TUser,
  TFilterModel,
} from "../../../types/config";
import {
  eColumnConfigKeys,
  eDEALER_VIEW_TYPE,
  eHTTPStatusCode,
  eORGANIZATION,
  ePrivileges,
  eResultCode,
} from "../../../utils/enum";
import {
  dateOnlyFormat,
  defaultFilterParams,
  STAKE_HOLDERS,
} from "../../../utils/constants";
import DateUtils from "../../../utils/date";
import Utils from "../../../utils";
import CustomDrawer from "../../../components/drawer";
import GenericPopover from "../../../components/pop-over";
import { TAttendance, TCustomer, TDealer, TDealerMaster } from "../../../types";
import {
  AddEditDealer,
  GetCustomerList,
  GetDealerList,
  GetDealerMasterList,
  GetUploadedADLData,
} from "../../../utils/api.constant";
import { FilterDropdownProps } from "antd/es/table/interface";
import ColumnManager from "../../../components/configurable-columns";
import ActionCard from "../../../components/actionCard/actionCard";
import AuthUtil from "../../../utils/auth";
import { ColumnConfigManager } from "../../../utils/column-management";
import styles from "./attendance.module.css";
import {
  GetServicePartnerList,
  GetCustMappedPartnerList,
} from "../../../utils/api.constant";
/**
 * DealerList is a component that is used to render the list of dealers.
 * It fetches the list of dealers from the API and renders it in a table.
 * It also provides the functionality to add a new dealer, edit an existing one and delete a dealer.
 */

export default function AtttendanceList() {
  // State and hooks initialization
  const navigate = useNavigate();
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<TAttendance[]>([]);
  const [dealerMasterOptions, setDealerMasterOptions] = useState<
    TDropdownOptions[]
  >([]);
  const [servicePartnerOptions, setServicePartnerOptions] = useState<
    TDropdownOptions[]
  >([]);
  const [isActiveList, setIsActiveList] = useState<boolean>();
  const [filterParams, setFilterParams] = useState<TFilterModel>({
    ...defaultFilterParams,
  });
  const context: any = useContext(UserContext);
  const [activeUser, setActiveUser] = useState<TUser>(context?.user);
  const [pageSize, setPageSize] = useState(10);
  const [globalsearchText, setGlobalSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [attendanceData, setAttendanceData] = useState<TAttendance | null>(
    null
  );
  const [selectedDate, setSelectedDate] = useState<string>(
    dayjs().format("DD-MMM-YYYY")
  );

  const handleDateChange = (
    date: Dayjs | null,
    dateString: string | string[]
  ) => {
    if (!date) {
      setSelectedDate(""); // Clear the date when the picker is cleared
    } else if (Array.isArray(dateString)) {
      console.warn("Unexpected dateString as array:", dateString); // Handle unexpected array case
    } else {
      setSelectedDate(dateString); // Update state with formatted date
      console.log("Selected Date:", dateString);
    }
  };

  useEffect(() => {
    if (context?.user?.stakeHolderName === STAKE_HOLDERS.IDEX) {
      getServicePartners();
    } else if (
      context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER &&
      context?.user?.orgId
    ) {
      getCustomerMappedServicePartners(context?.user?.orgId);
    } else if (
      context?.user?.stakeHolderName === STAKE_HOLDERS.SERVICEPARTNER &&
      context?.user?.orgId
    ) {
      setFilterParams((prev) => ({
        ...prev,
        servicePartnerId: context?.user?.orgId || undefined,
      }));
    }
  }, [context?.user]);

  useEffect(() => {
    fetchData();
  }, [filterParams.servicePartnerId, selectedDate]);

  useEffect(() => {
    const newUrl = new URL(window.location.href);
    const servicePartnerId = newUrl.searchParams.get("servicePartnerId");

    if (servicePartnerId) {
      setFilterParams((prev) => ({
        ...prev,
        servicePartnerId: parseInt(servicePartnerId, 10),
      }));
    } else if (
      servicePartnerOptions.length > 0 &&
      !filterParams.servicePartnerId
    ) {
      const firstOption = servicePartnerOptions[0];
      setFilterParams((prev) => ({
        ...prev,
        servicePartnerId: firstOption.value as number,
        servicePartnerName: firstOption.label,
      }));
    }
  }, [servicePartnerOptions]);

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<TAttendance> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder="Filter by keyword"
          prefix={
            <SearchOutlined
              style={{ color: "rgba(0,0,0,.45)", marginRight: 4 }}
            />
          } // Adds and spaces the search icon
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{
            marginBottom: 8,
            display: "flex",
            paddingLeft: "12px", // Ensure spacing for both icon and text
          }}
        />
        <Space style={{ display: "flex", justifyContent: "space-between" }}>
          {/* RESET FILTERS */}
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            type="link"
            size="small"
            style={{ width: 45 }}
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            // icon={<SearchOutlined />}
            size="small"
            style={{ width: 50 }}
          >
            Ok
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterFilled
        style={{ color: filtered ? "var(--bs-primary)" : undefined }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "var(--bs-primary)",
            padding: 0,
          }}
          searchWords={[globalsearchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const commonSorter = (a: any, b: any, key: string) => {
    const valueA = a[key] as string;
    const valueB = b[key] as string;
    return valueA?.localeCompare(valueB);
  };

  const initialColumns: TableColumnsType<TAttendance> = [
    {
      title: "Sr. No.",
      dataIndex: "srNo",
      key: "srNo",
      width: 70,
      render: (_text, _record, index) => index + 1,
    },
    {
      title: "Service Engineer",
      dataIndex: "serviceEngineerName",
      key: "serviceEngineer",
      width: 170,
      ...getColumnSearchProps("serviceEngineerName"),
      filterSearch: true,
      sorter: (a, b) => commonSorter(a, b, "serviceEngineerName"),
    },
    {
      title: "Status",
      dataIndex: "serviceEngineerStatus",
      key: "serviceEngineerStatus",
      filters: [
        { text: "Present", value: 1 },
        { text: "Absent", value: 0 },
      ],
      onFilter: (value, record) =>
        record.serviceEngineerStatus.toString().includes(value.toString()),
      width: 150,
      sorter: (a, b) => commonSorter(a, b, "serviceEngineerStatus"),
      filterSearch: true,
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const { onShowToast } = useToast();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null); // State to keep track of selected user for modal
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);
  type DataIndex = keyof TAttendance;

  const fetchData = async () => {
    if (filterParams.servicePartnerId) {
      setIsLoading(true);
      try {
        let { servicePartnerId, pageSize, currentPage, ...restFilters } =
          filterParams;
        const requestPayload = {
          data: {
            pageSize: -1,
            currentPage: 1,
            ...restFilters,
            servicePartnerId,
          },
        };

        const response: TResponseModel = await post(
          GetDealerList,
          requestPayload
        );
        const { dataResponse, data, filterModel } = response;
        if (dataResponse.returnCode === eResultCode.SUCCESS) {
          setAttendanceData(data);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: dataResponse.description,
          });
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error:", error);
        setIsLoading(false); // setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getCustomerMappedServicePartners = async (customerId: string) => {
    try {
      const payload = {
        data: {
          customerId,
          pageSize: -1,
          currentPage: 1,
        },
      };

      const response: TResponseModel = await post(
        GetCustMappedPartnerList,
        payload
      );

      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const mappedOption = Utils.mapDropdownOptions(data);
          setServicePartnerOptions(mappedOption);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching mapped partners:", error);
    }
  };

  const getServicePartners = async () => {
    try {
      const payload = {
        data: {
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetServicePartnerList,
        payload
      );
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const mappedOption = Utils.mapDropdownOptions(data);
          setServicePartnerOptions(mappedOption);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setGlobalSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  // Helper to generate unique filter options

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setGlobalSearchText("");
  };

  const filteredData = data.filter((item) =>
    Object.values(item).some((val) =>
      val.toString().toLowerCase().includes(globalsearchText.toLowerCase())
    )
  );

  console.log(selectedDate);

  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardOutlined />}
        title={`${filterParams?.totalRows} Total Service Engineers`}
        size="large"
        className={styles.pageHeaderStyles}
      >
        <div className={styles.headerParentWrap}>
          <div style={{ display: "flex" }}>
            <Select
              disabled={
                context?.user?.stakeHolderName ===
                  STAKE_HOLDERS.SERVICEPARTNER || isLoading
                  ? true
                  : false
              }
              variant="borderless"
              placeholder="Select Service Partner"
              style={{ width: "15vw" }}
              onChange={(value, data) => {
                const newurl = new URL(window.location.href);
                newurl.searchParams.set("servicePartnerId", value.toString());
                newurl.searchParams.set(
                  "servicePartnerName",
                  (data as any)?.label.toString()
                );
                window.history.pushState({}, "", newurl.toString());
                setFilterParams((prev) => ({
                  ...filterParams,
                  servicePartnerId: value,
                  servicePartnerName: (data as any)?.label,
                }));
              }}
              value={filterParams.servicePartnerId || null}
              filterOption={(input, option) =>
                option?.label
                  ? option.label.toLowerCase().includes(input.toLowerCase())
                  : false
              }
              showSearch
              options={servicePartnerOptions}
            />
          </div>
          <div className={styles.headerRightSection}>
            <DatePicker
              format="DD-MMM-YYYY"
              value={selectedDate ? dayjs(selectedDate, "DD-MMM-YYYY") : null} // Set value as null if date is cleared
              onChange={handleDateChange}
              placeholder="Select Date"
              style={{ width: 100 }}
            />
            <Input
              placeholder="Filter by keywords"
              value={globalsearchText}
              onChange={(e) => setGlobalSearchText(e.target.value)}
              style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
              prefix={<SearchOutlined />}
            />
          </div>
        </div>
      </PageHeader>
      <div className="tableWrap">
        <Table<TAttendance>
          // key={tableKey}
          scroll={{
            x: "max-content", // Horizontal scrolling
            y: "calc(100vh - 280px)", // Adjust height dynamically
          }}
          columns={columns}
          loading={isLoading}
          dataSource={filteredData}
          onChange={handleTableChange}
          pagination={{
            // ...pagination,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
            showSizeChanger: true,
            onShowSizeChange: (_, size) => setPageSize(size),
            // showQuickJumper: true,
            position: ["bottomLeft"],
          }}
          sticky
          // size="small"
        />
      </div>
    </div>
  );
}
